import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Shuffle, X } from 'lucide-react';

const TRANSITION_TYPES = ['cut', 'crossfade', 'fade_through_black', 'wipe', 'slide', 'zoom'];
const DIRECTIONS = ['left', 'right', 'up', 'down'];
const LABELS = { cut: 'Cut', crossfade: 'Crossfade / Dissolve', fade_through_black: 'Fade Through Black', wipe: 'Wipe', slide: 'Slide', zoom: 'Zoom' };

/**
 * Transition applied at this VIDEO item's start, blending with whatever
 * ends there on the same track. Genuinely rendered in the shared preview
 * (EditorCompositionPreview.jsx composites the outgoing + incoming clip
 * with real CSS opacity/clip-path/transform over the transition window)
 * — never a decorative no-op control. Duration is clamped to this item's
 * own duration server-side so it can never reach past available media.
 */
export function EditorTransitionsPanel({ item, onTransitionChange, onRemoveTransition }) {
  if (item.item_type !== 'video') return null;
  const transition = item.transition_in || { type: 'cut', duration_seconds: 0.5, direction: null };
  const isDirectional = transition.type === 'wipe' || transition.type === 'slide';
  const maxDuration = Math.max(0.2, Math.min(5, item.duration_seconds));

  return (
    <div className="space-y-3 pt-2 border-t border-white/5" data-testid="transitions-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Shuffle className="h-3 w-3" />Transition (In)</p>
        {transition.type !== 'cut' && (
          <button onClick={onRemoveTransition} className="text-red-400 hover:text-red-300" data-testid="transition-remove-button"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <Select value={transition.type} onValueChange={(v) => onTransitionChange({ type: v, direction: (v === 'wipe' || v === 'slide') ? (transition.direction || 'left') : null })}>
        <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="transition-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{TRANSITION_TYPES.map((t) => <SelectItem key={t} value={t}>{LABELS[t]}</SelectItem>)}</SelectContent>
      </Select>
      {transition.type !== 'cut' && (
        <>
          <div className="space-y-1.5">
            <label className="text-xs text-[#8A93A6]">Duration (s)</label>
            <div className="flex items-center gap-3">
              <Slider value={[Math.min(transition.duration_seconds ?? 0.5, maxDuration)]} min={0.1} max={maxDuration} step={0.1} onValueChange={([v]) => onTransitionChange({ duration_seconds: v })} className="flex-1" data-testid="transition-duration-slider" />
              <span className="text-xs text-white w-10 text-right">{(transition.duration_seconds ?? 0.5).toFixed(1)}s</span>
            </div>
          </div>
          {isDirectional && (
            <Select value={transition.direction || 'left'} onValueChange={(v) => onTransitionChange({ direction: v })}>
              <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="transition-direction-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <p className="text-[10px] text-[#555E6E]">Plays automatically where this clip overlaps the one before it on the same track — scrub the playhead across the cut to preview it.</p>
        </>
      )}
    </div>
  );
}
